import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconPlayerPause,
  IconPlayerPlay,
  IconArrowLeft,
} from '@tabler/icons-react';
import type { Campaign, Advertiser, AdCreative, AdLevel } from '../types';
import {
  getAllCampaigns,
  getCampaignsByAdvertiser,
  createCampaign,
  updateCampaign,
  toggleCampaignActive,
  deleteCampaign,
} from '../services/campaignsService';
import { getAdvertisers } from '../services/advertisersService';
import AdImageDropzone from '../components/AdImageDropzone';

// ---------- types ----------

interface LevelForm {
  enabled: boolean;
  imageUrl: string;
  tapUrl: string;
  rotationSeconds: number;
}

interface FormState {
  advertiserId: string;
  name: string;
  priority: number;
  isActive: boolean;
  startDate: string;
  endDate: string;
  levels: Record<AdLevel, LevelForm>;
}

const DEFAULT_LEVEL: LevelForm = { enabled: false, imageUrl: '', tapUrl: '', rotationSeconds: 5 };

const DEFAULT_FORM: FormState = {
  advertiserId: '',
  name: '',
  priority: 99,
  isActive: false,
  startDate: '',
  endDate: '',
  levels: {
    1: { ...DEFAULT_LEVEL },
    2: { ...DEFAULT_LEVEL },
    3: { ...DEFAULT_LEVEL },
  },
};

const LEVEL_LABELS: Record<AdLevel, string> = {
  1: 'Level 1 — Home',
  2: 'Level 2 — Section screens (News, Forum, Market…)',
  3: 'Level 3 — Detail screens (Article, WebView, Pricing…)',
};

// ---------- helpers ----------

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString();
}

function scheduleLabel(campaign: Campaign): string {
  if (!campaign.startDate && !campaign.endDate) return 'Open-ended';
  const from = campaign.startDate ? formatDate(campaign.startDate) : '∞';
  const to = campaign.endDate ? formatDate(campaign.endDate) : '∞';
  return `${from} → ${to}`;
}

function levelFormFromCreative(creative: AdCreative | undefined): LevelForm {
  if (!creative?.imageUrl) return { ...DEFAULT_LEVEL };
  return {
    enabled: true,
    imageUrl: creative.imageUrl,
    tapUrl: creative.tapUrl,
    rotationSeconds: creative.rotationSeconds,
  };
}

function levelsToPayload(levels: Record<AdLevel, LevelForm>): Partial<Record<AdLevel, AdCreative>> {
  const result: Partial<Record<AdLevel, AdCreative>> = {};
  ([1, 2, 3] as AdLevel[]).forEach(l => {
    if (levels[l].enabled && levels[l].imageUrl) {
      result[l] = {
        imageUrl: levels[l].imageUrl,
        tapUrl: levels[l].tapUrl,
        rotationSeconds: levels[l].rotationSeconds,
      };
    }
  });
  return result;
}

// ---------- component ----------

export default function CampaignsPage() {
  // Scoped mode: /advertisers/:advertiserId/campaigns
  // Global mode: /campaigns
  const { advertiserId: paramAdvertiserId } = useParams<{ advertiserId?: string }>();
  const isScoped = Boolean(paramAdvertiserId);
  const navigate = useNavigate();

  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([]);
  const [filterAdvertiserId, setFilterAdvertiserId] = useState<string>(paramAdvertiserId ?? '');
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [modalCampaignId, setModalCampaignId] = useState<string>('');
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    setFilterAdvertiserId(paramAdvertiserId ?? '');
  }, [paramAdvertiserId]);

  useEffect(() => {
    loadData();
  }, [paramAdvertiserId]);

  async function loadData() {
    try {
      setLoading(true);
      const [advs, camps] = await Promise.all([
        getAdvertisers(),
        isScoped ? getCampaignsByAdvertiser(paramAdvertiserId!) : getAllCampaigns(),
      ]);
      setAdvertisers(advs);
      setAllCampaigns(camps);
    } catch (err) {
      console.error('Error loading campaigns:', err);
    } finally {
      setLoading(false);
    }
  }

  const displayedCampaigns = filterAdvertiserId
    ? allCampaigns.filter(c => c.advertiserId === filterAdvertiserId)
    : allCampaigns;

  function advertiserName(id: string): string {
    return advertisers.find(a => a.id === id)?.name ?? id;
  }

  // ---------- modal ----------

  function openAdd() {
    const tempId = `tmp-${Date.now()}`;
    setModalCampaignId(tempId);
    setEditing(null);
    setForm({
      ...DEFAULT_FORM,
      advertiserId: filterAdvertiserId || advertisers[0]?.id || '',
      levels: {
        1: { ...DEFAULT_LEVEL },
        2: { ...DEFAULT_LEVEL },
        3: { ...DEFAULT_LEVEL },
      },
    });
    setFormError(null);
    setShowModal(true);
  }

  function openEdit(campaign: Campaign) {
    setModalCampaignId(campaign.id);
    setEditing(campaign);
    setForm({
      advertiserId: campaign.advertiserId,
      name: campaign.name,
      priority: campaign.priority,
      isActive: campaign.isActive,
      startDate: campaign.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : '',
      endDate: campaign.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : '',
      levels: {
        1: levelFormFromCreative(campaign.levels[1]),
        2: levelFormFromCreative(campaign.levels[2]),
        3: levelFormFromCreative(campaign.levels[3]),
      },
    });
    setFormError(null);
    setShowModal(true);
  }

  function setLevelField(level: AdLevel, field: keyof LevelForm, value: string | number | boolean) {
    setForm(f => ({
      ...f,
      levels: { ...f.levels, [level]: { ...f.levels[level], [field]: value } },
    }));
  }

  // ---------- save ----------

  async function handleSave() {
    if (!form.name.trim()) {
      setFormError('Campaign name is required');
      return;
    }
    if (!form.advertiserId) {
      setFormError('Please select an advertiser');
      return;
    }

    const enabledLevels = ([1, 2, 3] as AdLevel[]).filter(l => form.levels[l].enabled);
    if (enabledLevels.length === 0) {
      setFormError('Enable at least one level with an ad creative');
      return;
    }

    const missingImages = enabledLevels.filter(l => !form.levels[l].imageUrl);
    if (missingImages.length > 0) {
      setFormError(`Upload images for: ${missingImages.map(l => `Level ${l}`).join(', ')}`);
      return;
    }

    const missingUrls = enabledLevels.filter(l => !form.levels[l].tapUrl.trim());
    if (missingUrls.length > 0) {
      setFormError(`Enter tap URLs for: ${missingUrls.map(l => `Level ${l}`).join(', ')}`);
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        advertiserId: form.advertiserId,
        name: form.name.trim(),
        priority: Number(form.priority),
        isActive: form.isActive,
        startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
        endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
        levels: levelsToPayload(form.levels),
      };

      if (editing) {
        await updateCampaign(editing.id, payload);
      } else {
        await createCampaign(payload);
      }
      setShowModal(false);
      await loadData();
    } catch (err) {
      console.error('Error saving campaign:', err);
      setFormError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(campaign: Campaign) {
    try {
      await toggleCampaignActive(campaign.id, campaign.isActive);
      await loadData();
    } catch (err) {
      console.error('Error toggling campaign:', err);
      alert('Failed to update campaign status');
    }
  }

  async function handleDelete(campaign: Campaign) {
    if (
      !confirm(
        `Delete campaign "${campaign.name}"? This will also remove all associated ad images.`
      )
    )
      return;
    try {
      await deleteCampaign(campaign.id);
      await loadData();
    } catch (err) {
      console.error('Error deleting campaign:', err);
      alert('Failed to delete campaign');
    }
  }

  // ---------- render ----------

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const scopedAdvertiser = isScoped ? advertisers.find(a => a.id === paramAdvertiserId) : null;

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          {isScoped ? (
            <>
              <div className="page-pretitle d-flex align-items-center gap-2">
                <button
                  className="btn btn-link p-0 text-muted"
                  onClick={() => navigate('/advertisers')}
                >
                  <IconArrowLeft size={16} className="me-1" />
                  Advertisers
                </button>
                <span>/</span>
                <span>{scopedAdvertiser?.name ?? paramAdvertiserId}</span>
              </div>
              <h2 className="page-title">Campaigns</h2>
            </>
          ) : (
            <>
              <div className="page-pretitle">Advertising</div>
              <h2 className="page-title">Campaigns</h2>
            </>
          )}
        </div>

        <div className="col-auto">
          <button onClick={openAdd} className="btn btn-primary">
            <IconPlus className="icon" />
            New Campaign
          </button>
        </div>
      </div>

      {/* Filters — global mode only */}
      {!isScoped && (
        <div className="card mt-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Advertiser</label>
                <select
                  className="form-select"
                  value={filterAdvertiserId}
                  onChange={e => setFilterAdvertiserId(e.target.value)}
                >
                  <option value="">All advertisers</option>
                  {advertisers.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card mt-3">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                {!isScoped && <th>Advertiser</th>}
                <th>Campaign</th>
                <th>Levels</th>
                <th>Priority</th>
                <th>Schedule</th>
                <th>Status</th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedCampaigns.length === 0 ? (
                <tr>
                  <td colSpan={isScoped ? 6 : 7} className="text-center text-muted py-4">
                    No campaigns yet
                  </td>
                </tr>
              ) : (
                displayedCampaigns.map(campaign => (
                  <tr key={campaign.id}>
                    {!isScoped && (
                      <td className="text-muted small">{advertiserName(campaign.advertiserId)}</td>
                    )}
                    <td>{campaign.name}</td>
                    <td>
                      <div className="d-flex gap-1">
                        {([1, 2, 3] as AdLevel[]).map(l => (
                          <span
                            key={l}
                            className={`badge ${
                              campaign.levels[l] ? 'bg-blue-lt' : 'bg-secondary-lt text-secondary'
                            }`}
                            title={LEVEL_LABELS[l]}
                          >
                            L{l}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{campaign.priority}</td>
                    <td className="text-muted small">{scheduleLabel(campaign)}</td>
                    <td>
                      <span
                        className={`badge text-white ${
                          campaign.isActive ? 'bg-success' : 'bg-secondary'
                        }`}
                      >
                        {campaign.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <button
                          onClick={() => handleToggle(campaign)}
                          className="btn btn-icon"
                          title={campaign.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {campaign.isActive ? (
                            <IconPlayerPause className="icon" />
                          ) : (
                            <IconPlayerPlay className="icon" />
                          )}
                        </button>
                        <button
                          onClick={() => openEdit(campaign)}
                          className="btn btn-icon"
                          title="Edit"
                        >
                          <IconPencil className="icon" />
                        </button>
                        <button
                          onClick={() => handleDelete(campaign)}
                          className="btn btn-icon text-danger"
                          title="Delete"
                        >
                          <IconTrash className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block', overflowY: 'auto' }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editing ? 'Edit Campaign' : 'New Campaign'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                {formError && <div className="alert alert-danger mb-3">{formError}</div>}

                {/* Advertiser selector — global mode: dropdown on create, read-only on edit */}
                {!isScoped && (
                  <div className="mb-3">
                    <label className="form-label">Advertiser</label>
                    {editing ? (
                      <input
                        type="text"
                        className="form-control"
                        value={advertiserName(form.advertiserId)}
                        disabled
                      />
                    ) : (
                      <select
                        className="form-select"
                        value={form.advertiserId}
                        onChange={e => setForm(f => ({ ...f, advertiserId: e.target.value }))}
                      >
                        <option value="">— Select advertiser —</option>
                        {advertisers.map(a => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Basic fields */}
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Campaign name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Spring 2026"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      autoFocus
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Priority</label>
                    <input
                      type="number"
                      className="form-control"
                      min={1}
                      value={form.priority}
                      onChange={e => setForm(f => ({ ...f, priority: Number(e.target.value) }))}
                    />
                    <div className="form-hint">Lower = shown first</div>
                  </div>
                  <div className="col-md-3 d-flex align-items-center pt-4">
                    <label className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={form.isActive}
                        onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                      />
                      <span className="form-check-label">Active</span>
                    </label>
                  </div>
                </div>

                {/* Schedule */}
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label">
                      Start date <span className="text-muted">(optional)</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.startDate}
                      onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">
                      End date <span className="text-muted">(optional)</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.endDate}
                      onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                    />
                  </div>
                  <div className="col-12">
                    <div className="form-hint">Leave both blank for an open-ended campaign.</div>
                  </div>
                </div>

                <hr className="my-3" />
                <h6 className="mb-1">Ad Creatives</h6>
                <p className="text-muted small mb-3">
                  Enable the levels where this campaign should display a banner. At least one level
                  is required.
                </p>

                {([1, 2, 3] as AdLevel[]).map(level => {
                  const lf = form.levels[level];
                  return (
                    <div key={level} className="card mb-3">
                      <div className="card-header d-flex align-items-center justify-content-between">
                        <h6 className="card-title mb-0">{LEVEL_LABELS[level]}</h6>
                        <label className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={lf.enabled}
                            onChange={e => setLevelField(level, 'enabled', e.target.checked)}
                          />
                          <span className="form-check-label">Enable</span>
                        </label>
                      </div>
                      {lf.enabled && (
                        <div className="card-body">
                          <div className="mb-3">
                            <label className="form-label">Ad Image</label>
                            <AdImageDropzone
                              campaignId={modalCampaignId}
                              level={level}
                              currentImageUrl={lf.imageUrl || undefined}
                              onUploaded={url => setLevelField(level, 'imageUrl', url)}
                            />
                          </div>
                          <div className="row g-3">
                            <div className="col-md-8">
                              <label className="form-label">Tap URL</label>
                              <input
                                type="url"
                                className="form-control"
                                placeholder="https://example.com"
                                value={lf.tapUrl}
                                onChange={e => setLevelField(level, 'tapUrl', e.target.value)}
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Duration (seconds)</label>
                              <input
                                type="number"
                                className="form-control"
                                min={1}
                                max={60}
                                value={lf.rotationSeconds}
                                onChange={e =>
                                  setLevelField(level, 'rotationSeconds', Number(e.target.value))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-auto"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Create campaign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
